import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createUser, updateUser, fetchUsers } from "../api/user";
import {
  fetchProfile,
  updateProfile,
  fetchProfileVersions,
  revertProfileVersion,
} from "../api/profile";
import produce from "immer";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  user: {},
  profile: null,
  profileVersions: [],
  data: [],
  currentCount: 0,
  totalCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 1,
  query: "",
  sort: "desc",
  sortBy: "id",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const updateUserAsync = createAsyncThunk(
  "users/updateUserAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateUser(payload);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "update_user");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_user");
    }
  }
);

export const createUserAsync = createAsyncThunk(
  "users/createUserAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createUser(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_user");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_user");
    }
  }
);

export const getUsersAsync = createAsyncThunk(
  "cases/getUsersAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchUsers(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_users");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_users");
    }
  }
);

export const fetchProfileAsync = createAsyncThunk(
  "users/fetchProfileAsync",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchProfile(params);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "fetch_profile");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "fetch_profile");
    }
  }
);

export const updateProfileAsync = createAsyncThunk(
  "users/updateProfileAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateProfile(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_profile");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_profile");
    }
  }
);

export const fetchProfileVersionsAsync = createAsyncThunk(
  "users/fetchProfileVersionsAsync",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchProfileVersions(params);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "fetch_profile_versions"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "fetch_profile_versions");
    }
  }
);

export const revertProfileVersionAsync = createAsyncThunk(
  "users/revertProfileVersionAsync",
  async ({ versionId, ...payload }, { rejectWithValue }) => {
    try {
      const response = await revertProfileVersion(versionId, payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "revert_profile_version"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "revert_profile_version");
    }
  }
);

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUsersAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })

      .addCase(getUsersAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newCount = action.payload.newCount;
      })

      .addCase(getUsersAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente brukerne.");
      })
      .addCase(updateUserAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateUserAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload.id
          );
          draftState.data[index] = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateUserAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere brukeren.");
      })
      .addCase(createUserAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createUserAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createUserAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette brukeren.");
      })
      .addCase(fetchProfileAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(fetchProfileAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) state.profile = action.payload;
      })
      .addCase(fetchProfileAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente profilen.");
      })
      .addCase(updateProfileAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) state.profile = action.payload;
      })
      .addCase(updateProfileAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere profilen.");
      })
      .addCase(fetchProfileVersionsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profileVersions = action.payload?.data || [];
      })
      .addCase(fetchProfileVersionsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente profilhistorikk."
        );
      })
      .addCase(revertProfileVersionAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) state.profile = action.payload;
      })
      .addCase(revertProfileVersionAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke tilbakestille profilen."
        );
      });
  },
});

export const { reducer } = userSlice;

export default userSlice;
